import { useEffect, useRef, useState } from 'react';
import { redirectToHostedUi, OAuthError } from '@goodsteward/gs-auth-spa';
import { getCognitoImpersonationConfig } from '@/auth/config';
import { AuthShell } from './AuthShell';
import { Alert } from '@/components/ui/Alert';

// /impersonate-start — entry point for GS-staff impersonation in
// Cognito mode (per ADR 0007).
//
// Flow:
//   1. Admin SPA opens this URL in a new tab after admin-server has
//      written the impersonation grant to its DB.
//   2. We generate PKCE verifier + state in THIS tab's sessionStorage
//      and redirect to Cognito's authorize endpoint for the dedicated
//      impersonation app client.
//   3. The staffer's existing SSO cookie satisfies Cognito immediately
//      (no MFA prompt — the same cookie that signed them into admin
//      8h ago covers this). Cognito redirects to /impersonate-callback
//      with the code.
//
// PKCE state must be set by code at PMA's origin so the
// /impersonate-callback page (also at PMA's origin) can read it back.
// Setting state in admin's tab and reading it in PMA's tab would cross
// origins — sessionStorage is per-origin.
//
// Stub mode: not used. Admin opens `/impersonate#token=...` in stub
// mode (see ImpersonatePage.tsx) where the in-process minted token is
// passed via URL fragment.

export function ImpersonateStartPage() {
    const [error, setError] = useState<string | null>(null);
    // StrictMode would otherwise double-fire the redirect.
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        (async () => {
            try {
                const config = getCognitoImpersonationConfig();
                // returnTo: '/' — after the callback exchanges tokens
                // and the "acting as" banner appears, route to the
                // dashboard. The staffer can navigate from there.
                await redirectToHostedUi(config, { returnTo: '/' });
                // redirectToHostedUi calls location.assign — code
                // beyond this point only runs if the redirect failed.
            } catch (e) {
                const msg = e instanceof OAuthError
                    ? e.message
                    : e instanceof Error ? e.message : String(e);
                setError(msg);
            }
        })();
    }, []);

    return (
        <AuthShell showNotice={false}>
            {error ? (
                <Alert variant="danger">Impersonation failed to start: {error}</Alert>
            ) : (
                <p className="text-sm text-text-subtle">
                    Starting impersonation session…
                </p>
            )}
        </AuthShell>
    );
}
